import { Link } from 'react-router-dom';
import { Image as ImageIcon } from 'lucide-react';
import {
  formatPrice,
  getDiscountPercent,
  getSalePricing,
  getVariantProductPricing,
  useTranslation,
} from '@dukanti/shared';
import type { ProductWithImages, ProductWithVariants } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ProductActionsMenu } from './product-actions-menu';
import type {
  ProductAvailabilityState,
  ProductCatalogRow,
  ProductPublicationState,
} from './product-catalog-model';

export interface ProductsCatalogTableProps {
  readonly rows: ProductCatalogRow[];
  readonly storePublished: boolean;
  readonly onRequestDelete?: (product: ProductWithImages) => void;
  readonly onRequestPublish?: (product: ProductWithImages) => void;
  readonly onRequestUnpublish?: (product: ProductWithImages) => void;
}

function publicationVariant(state: ProductPublicationState): 'success' | 'neutral' {
  return state === 'published' ? 'success' : 'neutral';
}

function availabilityVariant(state: ProductAvailabilityState): 'success' | 'warning' | 'neutral' {
  if (state === 'available') return 'success';
  return 'neutral';
}

export function ProductsCatalogTable({
  rows,
  storePublished,
  onRequestDelete,
  onRequestPublish,
  onRequestUnpublish,
}: ProductsCatalogTableProps) {
  const { t, lang } = useTranslation();

  return (
    <div
      data-testid="products-table"
      className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.products.table.columns.product')}</TableHead>
            <TableHead>{t('admin.products.table.columns.category')}</TableHead>
            <TableHead>{t('admin.products.table.columns.price')}</TableHead>
            <TableHead>{t('admin.products.table.columns.inventory')}</TableHead>
            <TableHead>{t('admin.products.table.columns.publication')}</TableHead>
            <TableHead>{t('admin.products.table.columns.availability')}</TableHead>
            <TableHead className="text-end">{t('admin.products.table.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const salePricing =
              row.variantCount > 1
                ? {
                    ...getVariantProductPricing(row.product as ProductWithVariants),
                    discountPercent: getDiscountPercent({
                      price_cents: (row.product as ProductWithVariants).summary_regular_price_cents,
                      sale_price_cents: (row.product as ProductWithVariants)
                        .summary_sale_price_cents,
                    }),
                  }
                : getSalePricing(row.product);
            const effectivePrice = formatPrice(salePricing.effectivePriceCents, lang);
            const regularPrice = formatPrice(salePricing.regularPriceCents, lang);

            return (
              <TableRow key={row.product.id} data-testid={`products-table-row-${row.product.id}`}>
                <TableCell>
                  <Link
                    to={row.detailPath}
                    data-testid={`products-table-row-${row.product.id}-name`}
                    className="focus-visible:ring-zaatar group inline-flex min-w-0 items-center gap-3 rounded-sm text-start focus-visible:outline-none focus-visible:ring-2"
                  >
                    <span className="bg-admin-surface-muted border-border flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border">
                      {row.firstImageUrl ? (
                        <img
                          src={row.firstImageUrl}
                          alt={row.product.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon
                          className="text-muted-foreground h-5 w-5"
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                      )}
                    </span>
                    <div className="min-w-0">
                      <span className="text-foreground group-hover:text-zaatar-deep block truncate font-medium">
                        {row.product.name}
                      </span>
                      {row.variantCount > 1 ? (
                        <div
                          className="text-muted-foreground mt-1 flex flex-wrap items-center gap-1 text-xs"
                          data-testid={`products-table-row-${row.product.id}-variant-summary`}
                        >
                          <Badge variant="neutral">
                            {t('admin.products.table.variantSummary.count', {
                              count: row.variantCount,
                            })}
                          </Badge>
                          <Badge variant="neutral">
                            {t('admin.products.table.variantSummary.active', {
                              count: row.activeVariantCount,
                            })}
                          </Badge>
                          {row.hasDisabledVariants ? (
                            <Badge
                              variant="neutral"
                              data-testid={`products-table-row-${row.product.id}-disabled-variants`}
                            >
                              {t('admin.products.table.variantSummary.disabled')}
                            </Badge>
                          ) : null}
                          {row.hasOutOfStockVariants ? (
                            <Badge
                              variant="neutral"
                              data-testid={`products-table-row-${row.product.id}-out-of-stock-variants`}
                            >
                              {t('admin.products.table.variantSummary.outOfStock')}
                            </Badge>
                          ) : null}
                        </div>
                      ) : null}
                      <span className="text-muted-foreground block text-xs">{row.product.id}</span>
                    </div>
                  </Link>
                </TableCell>
                <TableCell data-testid={`products-table-row-${row.product.id}-category`}>
                  <Badge variant="neutral">{row.categoryName}</Badge>
                </TableCell>
                <TableCell
                  className="text-start"
                  data-testid={`products-table-row-${row.product.id}-price`}
                >
                  <div dir="ltr" className="font-display text-saffron font-semibold">
                    {effectivePrice}
                  </div>
                  {row.fromPrice ? (
                    <Badge
                      variant="neutral"
                      data-testid={`products-table-row-${row.product.id}-from-price`}
                      className="mt-1"
                    >
                      {t('admin.products.table.variantSummary.fromPrice')}
                    </Badge>
                  ) : null}
                  {salePricing.isOnSale ? (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <bdi
                        className="text-muted-foreground text-xs line-through"
                        data-testid={`products-table-row-${row.product.id}-regular-price`}
                      >
                        {regularPrice}
                      </bdi>
                      <Badge
                        variant="warning"
                        data-testid={`products-table-row-${row.product.id}-sale-badge`}
                      >
                        {t('admin.products.badge.onSale')}
                      </Badge>
                      {salePricing.discountPercent !== null ? (
                        <Badge
                          variant="default"
                          data-testid={`products-table-row-${row.product.id}-discount-badge`}
                        >
                          {t('admin.products.badge.discountPercent', {
                            percent: salePricing.discountPercent,
                          })}
                        </Badge>
                      ) : null}
                    </div>
                  ) : null}
                  {row.hasVariantSale ? (
                    <Badge
                      variant="warning"
                      data-testid={`products-table-row-${row.product.id}-variant-sale`}
                      className="mt-1"
                    >
                      {t('admin.products.table.variantSummary.sale')}
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell
                  className={cn('text-start', row.available <= 0 && 'text-destructive')}
                  data-testid={`products-table-row-${row.product.id}-inventory`}
                >
                  {t('admin.products.table.inventory.available', {
                    available: row.available,
                    inventory: row.inventory,
                  })}
                </TableCell>
                <TableCell data-testid={`products-table-row-${row.product.id}-publication`}>
                  <Badge variant={publicationVariant(row.publicationState)}>
                    {t(`admin.products.badge.${row.publicationState}`)}
                  </Badge>
                </TableCell>
                <TableCell data-testid={`products-table-row-${row.product.id}-availability`}>
                  <Badge variant={availabilityVariant(row.availabilityState)}>
                    {t(`admin.products.table.availability.${row.availabilityState}`)}
                  </Badge>
                </TableCell>
                <TableCell
                  className="text-end"
                  data-testid={`products-table-row-${row.product.id}-actions`}
                >
                  <div className="flex justify-end">
                    <ProductActionsMenu
                      product={row.product}
                      storePublished={storePublished}
                      onRequestDelete={onRequestDelete}
                      onRequestPublish={onRequestPublish}
                      onRequestUnpublish={onRequestUnpublish}
                      testIdPrefix={`products-table-row-${row.product.id}`}
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
